import React, { useEffect, useMemo, useState } from 'react'
import { Alert, Icon, Tabs } from 'antd'
import { ListLocale } from 'antd/lib/list'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'

import { FullRequestHotel, PolicyRule, ResumeHotel } from 'src/types'

import { ApplicationState } from 'src/store'
import {
  HotelSearchPeriod,
  HotelSearchPeriods
} from 'src/core/store/ducks/hotels/types'
import { setHotelsPeriodKey } from 'src/core/store/ducks/hotels/actions'
import ReducerOfSelectedHotelFilters from 'src/components/atoms/reducer-of-selected-hotel-filters'

import ListItem from './list-item'

interface Props {
  policyRule: PolicyRule
  selectedHotels: FullRequestHotel[]
  setSelectedItem: React.Dispatch<React.SetStateAction<ResumeHotel>>
  setVisibleModal: React.Dispatch<React.SetStateAction<boolean>>
}

interface TabsTabPaneRenderProps {
  hotelSearch: HotelSearchPeriod
  locale: ListLocale
  setSelectedItem: React.Dispatch<React.SetStateAction<ResumeHotel>>
  setVisibleModal: React.Dispatch<React.SetStateAction<boolean>>
}

function TabsTabPaneRender({
  hotelSearch,
  locale,
  setSelectedItem,
  setVisibleModal
}: TabsTabPaneRenderProps): JSX.Element {
  const { error, list } = hotelSearch

  if (error) {
    return <Alert closable={false} message={error} type="error" />
  }

  return (
    <ListItem
      list={list}
      locale={locale}
      setSelectedItem={setSelectedItem}
      setVisibleModal={setVisibleModal}
    />
  )
}

function MultiplePeriod({
  policyRule,
  selectedHotels,
  setSelectedItem,
  setVisibleModal
}: Props): JSX.Element {
  const {
    hotels: { currentPeriodKey, searches }
  } = useSelector((state: ApplicationState) => ({
    hotels: state.hotels
  }))

  const { t } = useTranslation()

  const dispatch = useDispatch()
  const [hotelSearches, setHotelsSearches] = useState<HotelSearchPeriods>({})

  const finishedLoading = useMemo(
    () =>
      Boolean(
        Object.keys(searches).length &&
          Object.values(searches).every(({ loading }) => !loading)
      ),
    [searches]
  )

  useEffect(() => {
    if (finishedLoading) {
      const hasCustomerSupplierPolicy =
        policyRule?.policy_request_general_rule?.service_by_customer_supplier

      if (!selectedHotels.length || !hasCustomerSupplierPolicy) {
        setHotelsSearches(
          Object.entries(searches).reduce(ReducerOfSelectedHotelFilters, {})
        )
        return
      }

      if (selectedHotels?.length > 1) {
        setHotelsSearches(
          Object.entries(searches).reduce(ReducerOfSelectedHotelFilters, {})
        )
        return
      }

      const filteredSearches = Object.entries(
        searches
      ).reduce<HotelSearchPeriods>((accSearches, [key, value]) => {
        const list = value.list.reduce((acc, cur) => {
          const hasRoomMathCustomerSupplier =
            cur.request_quote_hotel_rooms.filter(
              room =>
                room.customer_supplier_id ===
                selectedHotels[0].customer_supplier_id
            )

          if (hasRoomMathCustomerSupplier.length) {
            return [
              ...acc,
              {
                ...cur,
                request_quote_hotel_rooms: hasRoomMathCustomerSupplier
              }
            ]
          }
          return acc
        }, [])

        return {
          ...accSearches,
          [key]: {
            ...value,
            list
          }
        }
      }, {})
      setHotelsSearches(
        Object.entries(filteredSearches).reduce(
          ReducerOfSelectedHotelFilters,
          {}
        )
      )
      return
    }
    setHotelsSearches({})
  }, [finishedLoading, policyRule, searches, selectedHotels])

  if (!finishedLoading) {
    return <></>
  }

  return (
    <Tabs
      activeKey={currentPeriodKey}
      data-test-id="hotel-result-tab"
      defaultActiveKey={currentPeriodKey}
      onChange={activeKey => {
        dispatch(setHotelsPeriodKey(activeKey))

        // setPolicyListCheck(
        //   air.searches[activeKey].list[0]
        //     ? air.searches[activeKey].list[0].request_quote_airs
        //     : []
        // )
      }}
    >
      {Object.keys(hotelSearches).map(searchKey => (
        <Tabs.TabPane
          data-test-id={`hotel-result-tab-${searchKey}`}
          key={searchKey}
          tab={searchKey}
        >
          <TabsTabPaneRender
            hotelSearch={hotelSearches[searchKey]}
            locale={{
              emptyText: (
                <>
                  <Icon
                    type="info-circle"
                    style={{ fontSize: 28, paddingBottom: 4 }}
                  />
                  <br />
                  {t(
                    searches[searchKey]?.list?.length
                      ? 'web.services.hotels.filter-not-found'
                      : 'web.services.hotels.search-not-found'
                  )}
                </>
              )
            }}
            setSelectedItem={setSelectedItem}
            setVisibleModal={setVisibleModal}
          />
        </Tabs.TabPane>
      ))}
    </Tabs>
  )
}

export default MultiplePeriod
